import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getProviderMe, getProviderTrainings, getProviderRegistrations,
  getProviderInvoices, togglePublishTraining, deleteTraining,
  getOpleiderStripeStatus, createOpleiderStripeOnboardingLink, createOpleiderStripeDashboardLink
} from '../services/opleidingenApi';
import { Training } from '../types';
import { OpleiderSignalenFeed } from '../components/OpleiderSignalenFeed';
import {
  BookOpen, Users, Euro, Plus, Eye, EyeOff, Edit, Trash2,
  AlertCircle, TrendingUp, FileText, MessageSquare, CreditCard, ExternalLink
} from 'lucide-react';

/**
 * Stripe Connect-koppeling van de opleider. Academy-boekingen (werkgeversdeel
 * via de verzamelfactuur + privé-bijbetaling van de medewerker) worden als één
 * transfer naar dit account uitbetaald; zonder geverifieerde koppeling blijft
 * de uitbetaling openstaan.
 */
const StripeKoppelingKaart: React.FC = () => {
  const { t } = useTranslation('pages');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const { data, refetch } = useQuery({
    queryKey: ['opleider-stripe-status'],
    queryFn: getOpleiderStripeStatus,
  });
  const status = data?.onboarding_status ?? 'not_started';

  const foutmelding = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

  const koppel = async () => {
    setBezig(true); setFout(null);
    try {
      const { onboarding_url } = await createOpleiderStripeOnboardingLink();
      await refetch();
      // Account links zijn eenmalig en verlopen snel: meteen openen.
      if (onboarding_url) window.open(onboarding_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setFout(foutmelding(err, t('opleider.stripeLinkFailed')));
    } finally {
      setBezig(false);
    }
  };

  const openDashboard = async () => {
    setBezig(true); setFout(null);
    try {
      const { dashboard_url } = await createOpleiderStripeDashboardLink();
      if (dashboard_url) window.open(dashboard_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setFout(foutmelding(err, t('opleider.stripeDashboardFailed')));
    } finally {
      setBezig(false);
    }
  };

  const badge: Record<string, string> = {
    verified: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
    not_started: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className={`mb-6 p-4 rounded-xl border flex flex-wrap items-center justify-between gap-3 ${status === 'verified' ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'}`}>
      <div className="flex items-start gap-3">
        <CreditCard className="w-5 h-5 text-[#0B1F3A] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-gray-900 flex items-center gap-2">
            {t('opleider.stripeTitle')}
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge[status]}`}>
              {t(`opleider.stripeStatus_${status}`)}
            </span>
          </p>
          <p className="text-sm text-gray-600 mt-0.5">{t('opleider.stripeHint')}</p>
          {fout && <p className="text-sm text-red-600 mt-1" role="alert">{fout}</p>}
        </div>
      </div>
      {status === 'verified' ? (
        <button type="button" onClick={openDashboard} disabled={bezig}
          className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60">
          <ExternalLink className="w-4 h-4" />{t('opleider.stripeDashboard')}
        </button>
      ) : (
        <button type="button" onClick={koppel} disabled={bezig}
          className="flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459] disabled:opacity-60">
          <CreditCard className="w-4 h-4" />
          {status === 'not_started' ? t('opleider.stripeConnect') : t('opleider.stripeContinue')}
        </button>
      )}
    </div>
  );
};

export const OpleiderDashboardPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: provider, isLoading: loadingProvider } = useQuery({
    queryKey: ['provider-me'],
    queryFn: getProviderMe,
  });

  const { data: trainings = [], isLoading: loadingTrainings } = useQuery({
    queryKey: ['provider-trainings'],
    queryFn: getProviderTrainings,
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ['provider-registrations'],
    queryFn: () => getProviderRegistrations(),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ['provider-invoices'],
    queryFn: getProviderInvoices,
  });

  const toggleMutation = useMutation({
    mutationFn: togglePublishTraining,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-trainings'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTraining,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-trainings'] }),
  });

  if (loadingProvider) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1F3A]" /></div>;
  }

  // Redirect naar onboarding als contract niet getekend
  if (provider && !provider.contract_signed) {
    navigate('/opleider/onboarding');
    return null;
  }

  // Stats
  const totalAanmeldingen = registrations.length;
  const openAanmeldingen = registrations.filter((r) => r.status === 'aangemeld').length;
  const openFacturen = invoices.filter((f) => f.status === 'verzonden').length;
  const totaalOmzet = invoices
    .filter((f) => ['betaald', 'doorgestort'].includes(f.status))
    .reduce((sum, f) => sum + Number(f.bedrag_aan_provider || 0), 0);

  const gepubliceerd = trainings.filter((t) => t.is_published).length;

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('opleider.dashboard')}</h1>
            <p className="text-gray-500 mt-1">{t('opleider.welcomeBack', { name: provider?.organisatie_naam })}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/opleider/chat"
              className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              <MessageSquare className="w-4 h-4" />
              {t('opleider.chat')}
            </Link>
            <Link
              to="/opleider/trainings/nieuw"
              className="flex items-center gap-2 bg-[#0B1F3A] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#1A3459] transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('opleider.addTraining')}
            </Link>
          </div>
        </div>

        {/* Waarschuwing: profiel niet compleet */}
        {!provider?.profile_complete && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">{t('opleider.profileIncomplete')}</p>
              <p className="text-sm text-yellow-700 mt-0.5">
                {t('opleider.profileIncompleteHint')}{' '}
                <Link to="/opleider/onboarding" className="underline font-medium">{t('opleider.completeProfile')}</Link>
              </p>
            </div>
          </div>
        )}

        {/* Stripe-koppeling voor Academy-uitbetalingen */}
        <StripeKoppelingKaart />

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label={t('opleider.publishedTrainings')}
            value={gepubliceerd}
            total={trainings.length}
            icon={<BookOpen className="w-5 h-5" />}
            color="blue"
          />
          <StatCard
            label={t('opleider.totalRegistrations')}
            value={totalAanmeldingen}
            sub={`${openAanmeldingen} ${t('opleider.new')}`}
            icon={<Users className="w-5 h-5" />}
            color="purple"
          />
          <StatCard
            label={t('opleider.openInvoices')}
            value={openFacturen}
            icon={<FileText className="w-5 h-5" />}
            color="orange"
          />
          <StatCard
            label={t('opleider.totalReceived')}
            value={`€${totaalOmzet.toFixed(2).replace('.', ',')}`}
            icon={<TrendingUp className="w-5 h-5" />}
            color="green"
          />
        </div>

        {/* Menu 1: Actiecentrum & Meldingenfeed (12 signalen, Traffic Director) */}
        <OpleiderSignalenFeed />

        {/* Opleidingen tabel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">{t('opleider.myTrainings')}</h2>
            <div className="flex gap-3">
              <Link to="/opleider/registraties" className="text-sm text-[#0B1F3A] hover:underline font-medium">
                {t('opleider.registrations')} ({totalAanmeldingen})
              </Link>
              <Link to="/opleider/facturen" className="text-sm text-[#0B1F3A] hover:underline font-medium">
                {t('opleider.invoices')} ({invoices.length})
              </Link>
            </div>
          </div>

          {loadingTrainings ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" />
            </div>
          ) : trainings.length === 0 ? (
            <div className="p-10 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">{t('opleider.noTrainingsYet')}</p>
              <Link
                to="/opleider/trainings/nieuw"
                className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#1A3459] transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('opleider.addFirstTraining')}
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {trainings.map((training) => (
                <TrainingRow
                  key={training.id}
                  training={training}
                  onTogglePublish={() => toggleMutation.mutate(training.id)}
                  onEdit={() => navigate(`/opleider/trainings/${training.id}/bewerken`)}
                  onDelete={() => {
                    if (window.confirm(t('opleider.confirmDelete', { title: training.titel }))) {
                      deleteMutation.mutate(training.id);
                    }
                  }}
                  isLoading={toggleMutation.isPending || deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recente aanmeldingen */}
        {registrations.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{t('opleider.recentRegistrations')}</h2>
              <Link to="/opleider/registraties" className="text-sm text-[#0B1F3A] hover:underline">{t('opleider.viewAll')}</Link>
            </div>
            <div className="divide-y divide-gray-100">
              {registrations.slice(0, 5).map((reg) => (
                <div key={reg.id} className="px-5 py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {reg.first_name} {reg.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{reg.training_titel}</p>
                  </div>
                  <StatusBadge status={reg.status} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TrainingRow: React.FC<{
  training: Training & { aanmeldingen?: number };
  onTogglePublish: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isLoading: boolean;
}> = ({ training, onTogglePublish, onEdit, onDelete, isLoading }) => {
  const { t } = useTranslation('pages');
  return (
  <div
    className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
    onClick={onEdit}
  >
    {training.omslagfoto_url ? (
      <img src={training.omslagfoto_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
    ) : (
      <div className="w-12 h-12 rounded-lg bg-[#0B1F3A]/10 flex items-center justify-center flex-shrink-0">
        <BookOpen className="w-5 h-5 text-[#0B1F3A]/40" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-gray-900 truncate">{training.titel}</p>
      <div className="flex items-center gap-3 mt-0.5">
        {training.soort_opleiding && (
          <span className="text-xs text-gray-500">{training.soort_opleiding}</span>
        )}
        {training.prijs_excl_btw != null && (
          <span className="text-xs text-gray-500 flex items-center gap-0.5">
            <Euro className="w-3 h-3" />{parseFloat(String(training.prijs_excl_btw)).toFixed(2).replace('.', ',')}
          </span>
        )}
        <span className="text-xs text-gray-400">{training.aanmeldingen || 0} {t('opleider.registrationCount')}</span>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        training.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {training.is_published ? t('opleider.published') : t('opleider.concept')}
      </span>
      <button
        onClick={onTogglePublish}
        disabled={isLoading}
        className="p-1.5 text-gray-400 hover:text-[#0B1F3A] rounded-lg hover:bg-gray-100 transition-colors"
        title={training.is_published ? t('opleider.unpublish') : t('opleider.publish')}
      >
        {training.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      <button
        onClick={onEdit}
        className="p-1.5 text-gray-400 hover:text-[#0B1F3A] rounded-lg hover:bg-gray-100 transition-colors"
        title={t('opleider.edit')}
      >
        <Edit className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={isLoading}
        className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
        title={t('opleider.delete')}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string | number;
  total?: number;
  sub?: string;
  icon: React.ReactNode;
  color: 'blue' | 'purple' | 'orange' | 'green';
}> = ({ label, value, total, sub, icon, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-green-50 text-green-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}{total !== undefined && <span className="text-base font-normal text-gray-400">/{total}</span>}</div>
      <div className="text-sm text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation('pages');
  const map: Record<string, { label: string; cls: string }> = {
    aangemeld: { label: t('opleider.statusAangemeld'), cls: 'bg-blue-100 text-blue-700' },
    bevestigd: { label: t('opleider.statusBevestigd'), cls: 'bg-green-100 text-green-700' },
    betaald: { label: t('opleider.statusBetaald'), cls: 'bg-emerald-100 text-emerald-700' },
    geannuleerd: { label: t('opleider.statusGeannuleerd'), cls: 'bg-gray-100 text-gray-500' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-500' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

export default OpleiderDashboardPage;
